import { Permissions } from "@cnx/common";
import Auth from "components/Auth";
import { LoggedOut } from "components/LoggedOut";
import { ChatScrollProvider } from "components/Chat/ChatScrollContext";
import WithPushNotifications from "components/WithPushNotifications";
import { lazy, useEffect } from "react";
import { Navigate, Route, Routes, useParams } from "react-router";
import { useChatPanelStore } from "stores/chatPanel";
import AuthGuard from "./components/AuthGuard/AuthGuard";
import HomePage from "./components/HomePage";
import Layout from "./components/Layout";
import PrivateRoute from "./components/PrivateRoute";

const TerminalManagementRouting = lazy(
  () => import("components/TerminalManagement/routing")
);
const TerminalsRouting = lazy(() => import("components/Terminals/routing"));
const ActivityConfirmationPage = lazy(
  () => import("components/ActivityConfirmationPage")
);
const ExceptionTypeRoutes = lazy(
  () => import("components/ExceptionTypeManagement")
);
const ExceptionCauseRoutes = lazy(
  () => import("components/ExceptionCausesManagment")
);
const ExceptionWaitingReasonsRoutes = lazy(
  () => import("components/ExceptionWaitingReasonsManagment")
);
const ExceptionsManagement = lazy(
  () => import("components/ExceptionsManagement")
);
const TripRoutes = lazy(() => import("components/ShippingManagement/routing"));
const EpicsRouting = lazy(() => import("./components/Epics/routing"));
const FeaturesRouting = lazy(() => import("./components/Features/routing"));
const InstallEn = lazy(() =>
  import("./components/Install").then(module => ({ default: module.InstallEn }))
);
const InstallFi = lazy(() =>
  import("./components/Install").then(module => ({ default: module.InstallFi }))
);

const RolesRouting = lazy(() => import("./components/Roles/routing"));
const StakeholdersRouting = lazy(
  () => import("./components/Stakeholders/routing")
);
const UsersRouting = lazy(() => import("./components/Users/routing"));

const ChatRedirect: React.FC = () => {
  const { discussionId } = useParams<{ discussionId: string }>();
  const setPanelDiscussionId = useChatPanelStore(
    store => store.setPanelDiscussionId
  );
  useEffect(() => {
    if (discussionId) setPanelDiscussionId(discussionId);
  }, [discussionId, setPanelDiscussionId]);
  return <Navigate to="/home" replace />;
};

const Routing: React.FC = ({}) => {
  return (
    <Routes>
      <Route path="/" element={<Auth />}></Route>
      <Route path="/install" element={<InstallEn />}></Route>
      <Route path="/asenna" element={<InstallFi />}></Route>
      <Route path="/logged-out" element={<LoggedOut />}></Route>

      <Route element={<AuthGuard />}>
        <Route
          element={
            <ChatScrollProvider>
              <WithPushNotifications />
            </ChatScrollProvider>
          }
        >
          <Route
            path={"/activity/confirmation"}
            element={<ActivityConfirmationPage />}
          />
          <Route element={<Layout />}>
            <Route path="/home" element={<HomePage />}></Route>
            <Route path="/chat/:discussionId" element={<ChatRedirect />} />

            <Route
              path="/epics/*"
              element={
                <PrivateRoute
                  rights={[
                    Permissions.RIGHTS_USER_STORIES_USER,
                    Permissions.RIGHTS_USER_STORIES_ADMIN,
                  ]}
                >
                  <EpicsRouting />
                </PrivateRoute>
              }
            ></Route>

            <Route
              path="/features/*"
              element={
                <PrivateRoute rights={[Permissions.RIGHTS_USE_RIGHTS_ADMIN]}>
                  <FeaturesRouting />
                </PrivateRoute>
              }
            ></Route>

            <Route
              path="/roles/*"
              element={
                <PrivateRoute
                  rights={[
                    Permissions.RIGHTS_ROLES_USER,
                    Permissions.RIGHTS_ROLES_ADMIN,
                  ]}
                >
                  <RolesRouting />
                </PrivateRoute>
              }
            ></Route>

            <Route
              path="/trips/*"
              element={
                <PrivateRoute
                  rights={[
                    Permissions.RIGHTS_SHIPMENTS_USER,
                    Permissions.RIGHTS_SHIPMENTS_ADMIN,
                  ]}
                >
                  <TripRoutes />
                </PrivateRoute>
              }
            ></Route>

            <Route
              path="/terminals/*"
              element={
                <PrivateRoute rights={[Permissions.RIGHTS_TERMINAL_ADMIN]}>
                  <TerminalsRouting />
                </PrivateRoute>
              }
            />

            <Route path="/terminal-management">
              <Route
                path="*"
                element={
                  <PrivateRoute
                    rights={[
                      Permissions.RIGHTS_TERMINAL_USER,
                      Permissions.RIGHTS_TERMINAL_ADMIN,
                    ]}
                  >
                    <TerminalManagementRouting />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route
              path="/stakeholders/*"
              element={
                <PrivateRoute rights={[Permissions.RIGHTS_STAKEHOLDERS_ADMIN]}>
                  <StakeholdersRouting />
                </PrivateRoute>
              }
            ></Route>

            <Route
              path="/users/*"
              element={
                <PrivateRoute
                  rights={[
                    Permissions.RIGHTS_USERS_USER,
                    Permissions.RIGHTS_USERS_ADMIN,
                  ]}
                >
                  <UsersRouting />
                </PrivateRoute>
              }
            ></Route>

            <Route
              path="/exception-causes"
              element={
                <PrivateRoute rights={[Permissions.RIGHTS_EXCEPTIONS_ADMIN]}>
                  <ExceptionCauseRoutes />
                </PrivateRoute>
              }
            ></Route>

            <Route
              path="/exception-waiting-reasons"
              element={
                <PrivateRoute rights={[Permissions.RIGHTS_EXCEPTIONS_ADMIN]}>
                  <ExceptionWaitingReasonsRoutes />
                </PrivateRoute>
              }
            ></Route>

            <Route
              path="/exception-classes/*"
              element={
                <PrivateRoute
                  rights={[Permissions.RIGHTS_EXCEPTION_TYPE_ADMIN]}
                >
                  <ExceptionTypeRoutes />
                </PrivateRoute>
              }
            ></Route>

            <Route
              path="/exceptions"
              element={
                <PrivateRoute rights={[Permissions.RIGHTS_EXCEPTIONS_ADMIN]}>
                  <ExceptionsManagement />
                </PrivateRoute>
              }
            ></Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/home" />}></Route>
      </Route>
      <Route path="*" element={<Navigate to="/" />}></Route>
    </Routes>
  );
};

export default Routing;
